import * as actions from "./useSelection.actions"
import { Action, KeyRow, SelectedState, State } from "./useSelection.types"

export const getInitialState = (): State => ({
  items: {},
  expandedRowKeys: [],
  selectedParentIds: [],
  selectedNestedIds: [],
  unselectedIds: [],
  wasUpdated: false,
  selectedAll: false
})

export const getSelectedState = (
  selectedNestedIds: unknown[] | undefined,
  metadata?: { issuesTotal?: number }
): SelectedState => {
  if (!selectedNestedIds?.length || !metadata?.issuesTotal) return "none"

  if (selectedNestedIds.length === metadata?.issuesTotal) return "all"

  return "indeterminate"
}

export const getSelectedParentIds = (
  parentRowId: KeyRow,
  selectedParentIds: State["selectedParentIds"],
  selectedState?: SelectedState
) => {
  const nextSelectedParentIds = selectedParentIds.filter(
    (id) => id !== parentRowId
  )

  if (selectedState === "all" || selectedState === "indeterminate") {
    nextSelectedParentIds.push(parentRowId)
  }

  return nextSelectedParentIds
}

export const getNextSet = (arr: KeyRow[], id: KeyRow, addition?: boolean) =>
  addition ? arr.concat(id) : arr.filter((currentId) => currentId !== id)

export const reducer = (state: State, action: Action): State => {
  const { type } = action

  if (type === actions.SET_UPDATING_STATE_FOR_PARENT_ROWS) {
    const { parentRowIds, withErrorRowIds = [] } = action
    let wasUpdated = false

    const nextItems = parentRowIds.reduce(
      (acc, key) => {
        const parentRowId = `${key}`
        const currentProject = acc[parentRowId] || {}
        const updating = action.updating
        const selectedNestedIds = updating
          ? currentProject.selectedNestedIds
          : []
        const selectedState = updating ? currentProject.selectedState : "none"

        wasUpdated = !wasUpdated && !!currentProject.updating && !updating

        acc[parentRowId] = {
          ...currentProject,
          selectedAllInProject: !action.updating
            ? false
            : currentProject.selectedAllInProject,
          selectedNestedIds,
          selectedState,
          updating
        }

        return acc
      },
      { ...state.items }
    )

    withErrorRowIds.forEach((parentId) => {
      if (!nextItems[`${parentId}`]?.updating) return

      nextItems[`${parentId}`] = {
        ...nextItems[`${parentId}`],
        updating: false
      }
    })

    return {
      ...state,
      wasUpdated,
      items: nextItems,
      selectedNestedIds: !wasUpdated ? state.selectedNestedIds : [],
      selectedParentIds: state.selectedParentIds.filter(
        (id) => !parentRowIds.includes(id)
      )
    }
  }

  if (type === actions.SET_FLAGS_STATE) {
    const { payload } = action
    const { closeAllExpandedParentRows, unselect, ...rest } = payload
    const nextItemsState = unselect
      ? Object.keys(state.items || {}).reduce(
          (acc, key) => {
            const { nestedItems, metadata } = state.items[key] || {}

            acc[key] = { nestedItems, metadata }

            return acc
          },
          {} as typeof state.items
        )
      : state.items

    return {
      ...state,
      expandedRowKeys: closeAllExpandedParentRows ? [] : state.expandedRowKeys,
      selectedParentIds: unselect ? [] : state.selectedParentIds,
      selectedNestedIds: unselect ? [] : state.selectedNestedIds,
      items: nextItemsState,
      selectedAll: unselect ? false : state.selectedAll,
      ...rest
    }
  }

  if (action.type === actions.UNSELECT_ALL) {
    return {
      ...state,
      items: Object.keys(state.items).reduce(
        (acc, key) => {
          acc[key] = {
            ...state.items[key],
            selectedState: getSelectedState([]),
            selectedNestedIds: [],
            selectedAllInProject: false
          }

          return acc
        },
        {} as typeof state.items
      ),
      selectedParentIds: [],
      selectedNestedIds: []
    }
  }

  if (type === actions.SELECT_ALL) {
    const nextSelectedAll = action.payload ?? !state.selectedAll

    if (nextSelectedAll)
      return {
        ...state,
        selectedAll: nextSelectedAll
      }

    return {
      ...state,
      selectedAll: nextSelectedAll,
      params: {},
      unselectedIds: [],
      items: Object.keys(state.items).reduce(
        (acc, key) => {
          const item = state.items[key]

          acc[key] = {
            ...item,
            unselectedIds: []
          }

          return acc
        },
        {} as typeof state.items
      )
    }
  }

  if (type === actions.SET_UPDATING_ALL) {
    const selectAllIsUpdated = state.selectedAll && !action.payload

    return {
      ...state,
      ...(selectAllIsUpdated
        ? {
            params: {},
            unselectedIds: [],
            items: Object.keys(state.items).reduce(
              (acc, key) => {
                const item = state.items[key]

                acc[key] = {
                  ...item,
                  unselectedIds: []
                }

                return acc
              },
              {} as typeof state.items
            )
          }
        : {}),
      selectedAll: selectAllIsUpdated ? false : state.selectedAll,
      updating: action.payload
    }
  }

  if (type === actions.SELECT_ALL_ALLOCATABLE_ISSUES) {
    const { items, selectedParentIds } = state

    if (!items) return state

    return {
      ...state,
      items: Object.keys(state.items).reduce(
        (acc, key) => {
          const item = state.items[key]

          if (!item) return acc

          const { metadata, nestedItems } = item
          const issuesTotal = metadata?.issuesTotal || 0
          const currentIssueCount = nestedItems?.length || 0

          if (
            issuesTotal > currentIssueCount &&
            selectedParentIds.includes(key)
          )
            acc[key] = {
              ...item,
              selectedNestedIds: [...(item.nestedItemsIds || [])],
              selectedAllInProject: true
            }
          else acc[key] = item

          return acc
        },
        {} as typeof state.items
      )
    }
  }

  if (type === actions.UNSELECT_ROW) {
    const { parentRowId } = action
    const arr = parentRowId
      ? state.items[`${parentRowId}`]?.unselectedIds || []
      : state.unselectedIds
    const unselectedIds = getNextSet(arr, action.id, !action.selected)

    if (!parentRowId) {
      const { params } = state
      const { excludeProjectIds: _, ...rest } = params || {}
      const fallbackParams = params ? rest : params

      return {
        ...state,
        unselectedIds,
        params: unselectedIds.length
          ? { ...rest, excludeProjectIds: unselectedIds.join(",") }
          : fallbackParams
      }
    }

    const nextItems = {
      ...state.items,
      [`${parentRowId}`]: { ...state.items[`${parentRowId}`], unselectedIds }
    }
    const nextExcludeIssueIds = Object.keys(nextItems)
      .flatMap((key) => nextItems?.[key]?.unselectedIds || [])
      .join(",")
    const { params } = state
    const { excludeIssueIds: __, ...rest } = params || {}
    const fallbackParams = params ? rest : params

    return {
      ...state,
      items: nextItems,
      params: nextExcludeIssueIds.length
        ? { ...rest, excludeIssueIds: nextExcludeIssueIds }
        : fallbackParams
    }
  }

  const { parentRowId } = action
  const { selectedParentIds } = state
  const parentRowKey = `${parentRowId}`
  const currentRow = state.items[parentRowKey]

  switch (type) {
    case actions.SET_IDS_NESTED_TABLE: {
      const currentNestedItems = currentRow?.nestedItems
      const nextNestedItems = action.items
      let selectedNestedIds = currentRow?.selectedNestedIds

      if (!currentNestedItems) {
        const isRowSelected = state.selectedParentIds.includes(parentRowId)

        if (isRowSelected) {
          selectedNestedIds = nextNestedItems.map(({ id }) => id)
        }
      }

      const nextSelectedState = getSelectedState(
        selectedNestedIds,
        action.metadata
      )

      const nextExpandedRowKeys = !nextNestedItems.length
        ? state.expandedRowKeys.filter((key) => key !== parentRowId)
        : state.expandedRowKeys
      const nextSelectedParentIds = getSelectedParentIds(
        parentRowKey,
        selectedParentIds,
        nextSelectedState
      )
      const nextItem = {
        ...currentRow,
        selectedNestedIds,
        selectedState: nextSelectedState,
        nestedItems: nextNestedItems,
        nestedItemsIds: nextNestedItems.map(({ id }) => id),
        unselectedIds: [],
        metadata: action.metadata
      }

      return {
        ...state,
        expandedRowKeys: nextExpandedRowKeys,
        selectedParentIds: nextSelectedParentIds,
        items: {
          ...state.items,
          [parentRowKey]: nextItem
        }
      }
    }

    case actions.SELECT_ROWS_NESTED_TABLE: {
      const selectedState = getSelectedState(
        action.nestedRowIds,
        currentRow?.metadata
      )

      return {
        ...state,
        selectedParentIds: getSelectedParentIds(
          parentRowId,
          selectedParentIds,
          selectedState
        ),
        items: {
          ...state.items,
          [`${action.parentRowId}`]: {
            ...currentRow,
            selectedAllInProject: false,
            selectedState,
            selectedNestedIds: action.nestedRowIds
          }
        }
      }
    }

    case actions.SELECT_ALL_ROWS_NESTED_TABLE: {
      const { selected } = action
      const nestedItems = currentRow?.nestedItems
      const nextSelectedNestedIds = selected
        ? nestedItems?.map(({ id }) => id)
        : []
      let selectedState = getSelectedState(
        nextSelectedNestedIds,
        currentRow?.metadata
      )
      const issuesWouldNotLoad = !nestedItems && selectedState === "none"
      let nextExpandedRowKeys = state.expandedRowKeys

      // if issues would not load, we should select row and extend parent row
      if (issuesWouldNotLoad) {
        selectedState = "indeterminate"
        nextExpandedRowKeys = nextExpandedRowKeys.filter(
          (key) => key !== parentRowId
        )
        nextExpandedRowKeys.push(parentRowId)
      }
      const nextSelectedParentIds = getSelectedParentIds(
        parentRowKey,
        selectedParentIds,
        selectedState
      )

      return {
        ...state,
        expandedRowKeys: nextExpandedRowKeys,
        selectedParentIds: nextSelectedParentIds,
        items: {
          ...state.items,
          [parentRowKey]: {
            ...currentRow,
            selectedState,
            selectedAllInProject: false,
            selectedNestedIds: nextSelectedNestedIds
          }
        }
      }
    }

    case actions.EXPANDED_PARENT_ROW: {
      const { expanded = !state.expandedRowKeys.includes(parentRowId) } = action
      const nextExpandedRowKeys = expanded
        ? state.expandedRowKeys.concat(parentRowId)
        : state.expandedRowKeys.filter((key) => key !== parentRowId)

      return {
        ...state,
        expandedRowKeys: nextExpandedRowKeys
      }
    }

    case actions.SELECT_ROW: {
      const { nestedRowId, selected } = action
      const selectedNestedIds = currentRow?.selectedNestedIds || []
      const nextSelectedNestedIds = getNextSet(
        selectedNestedIds,
        nestedRowId,
        selected
      )
      const nextAllSelectedNestedIds = getNextSet(
        state.selectedNestedIds,
        nestedRowId,
        selected
      )

      return {
        ...state,
        selectedNestedIds: nextAllSelectedNestedIds,
        items: {
          ...state.items,
          [parentRowKey]: {
            ...currentRow,
            selectedNestedIds: nextSelectedNestedIds
          }
        }
      }
    }

    default:
      return state
  }
}
